import React, { memo, useCallback } from 'react';
import { Button, XStack, Circle } from 'tamagui';

import { CobuText } from '@cobu/ui';
import { useReactions, ReactionType } from '../../../hooks/useReactions';
import { GroupComment } from '@cobu/types';
import { useEmojiPalette } from '../../../features/reactions/context/EmojiProvider';

interface ReactionSummaryProps {
    comment: GroupComment;
    currentUserId: string;
    onReactToComment?: (commentId: string, reaction: string | null) => Promise<any>;
    onShowReactionModal?: (commentId: string) => void;
}

/**
 * Reaction Summary Component
 * 
 * Displays a summary of reactions on a comment with reaction counts
 * and clickable interface to view reaction details.
 * This component appears above the react/reply buttons.
 */
const ReactionSummary: React.FC<ReactionSummaryProps> = memo(({
    comment,
    currentUserId,
    onReactToComment,
    onShowReactionModal,
}) => {
    // Use the reactions hook
    const reactions = useReactions({
        comment,
        currentUserId,
        onReactToComment,
        onShowReactionModal,
    });

    const { byTitle } = useEmojiPalette();

    /**
     * Render reaction summary for comments with reactions
     */
    const renderReactionSummary = useCallback(() => {
        if (reactions.reactionsCount === 0) return null;

        const topReactions = reactions.reactionsSorted.slice(0, 2);

        return (
            <Button
                unstyled
                onPress={reactions.showReactionModal}
                py="$1"
                mb="$2"
                accessibilityRole="button"
                accessibilityLabel={`${reactions.reactionsCount} reactions. Double tap to view details`}
            >
                <XStack ai="center">
                    {/* Show top reaction emojis */}
                    <XStack gap="$1" mr="$2">
                        {topReactions.map((reactionType, index) => {
                            const config = reactions.getReactionConfig(reactionType as ReactionType);
                            const emojiItem = byTitle[reactionType];
                            const colorCandidate = emojiItem?.meta?.colorScheme || config?.color;
                            const color = (typeof colorCandidate === 'string' && colorCandidate.trim().length > 0)
                                ? colorCandidate
                                : '#999999';
                            return (
                                <Circle
                                    key={reactionType}
                                    size="$7"
                                    //@ts-ignore
                                    bc={color}
                                    borderWidth={2}
                                    borderColor="$background"
                                    ml={index > 0 ? -10 : 0}
                                    overflow="hidden"
                                    position="relative"
                                >
                                    {emojiItem?.url ? (
                                        <div 
                                            style={{ 
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <img 
                                                src={emojiItem.url} 
                                                alt=""
                                                style={{ 
                                                    width: 16, 
                                                    height: 16,
                                                    display: 'block',
                                                    flexShrink: 0
                                                }} 
                                            />
                                        </div>
                                    ) : (
                                        <div 
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <CobuText 
                                                fontSize={14}
                                                style={{
                                                    lineHeight: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                {emojiItem?.text || config?.emoji}
                                            </CobuText>
                                        </div>
                                    )}
                                </Circle>
                            );
                        })}
                    </XStack>

                    {/* Reaction count */}
                    <CobuText
                        variant="caption"
                        color="$colorSecondary"
                        fontSize="$2"
                        fow="500"
                    >
                        {reactions.formatReactionCount(reactions.reactionsCount)}
                    </CobuText>
                </XStack>
            </Button>
        );
    }, [reactions, byTitle]);

    return renderReactionSummary();
});

ReactionSummary.displayName = 'ReactionSummary';

export default ReactionSummary; 